import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { db } from '../lib/supabase'
import Button from '../components/Button'
import { User, FileText } from 'lucide-react'
import { useI18n } from '../i18n/I18nProvider'

function Profile() {
  const { t } = useI18n()
  const user = useAuthStore((state) => state.user)
  const updateProfile = useAuthStore((state) => state.updateProfile)
  
  const [username, setUsername] = useState('')
  const [bio, setBio] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    loadProfile()
  }, [user])

  const loadProfile = async () => {
    if (!user) return
    
    try {
      const profile = await db.getProfile(user.id)
      if (profile) {
        setUsername(profile.username || '')
        setBio(profile.bio || '')
        setAvatarUrl(profile.avatar_url || '')
      }
    } catch (error) {
      console.error('Failed to load profile:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    try {
      await db.updateProfile(user.id, {
        username,
        bio,
        avatar_url: avatarUrl,
      })
      
      await updateProfile({ username })
      
      setMessage(t('profile.updateSuccess'))
    } catch (error) {
      console.error('Failed to update profile:', error)
      setMessage(t('profile.updateError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-4xl font-bold text-green-800 mb-8 text-center">
          {t('profile.title')}
        </h1>

        <div className="bg-white rounded-xl shadow-lg p-8">
          {message && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User size={20} className="inline mr-2" />
                {t('profile.usernameLabel')}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t('profile.usernamePlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText size={20} className="inline mr-2" />
                {t('profile.bioLabel')}
              </label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t('profile.bioPlaceholder')}
                rows={4}
                maxLength={200}
              />
              <p className="text-sm text-gray-500 mt-1">
                {bio.length}/200 {t('profile.characters')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.avatarUrlLabel')}
              </label>
              <input
                type="url"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="https://exemple.com/avatar.jpg"
              />
            </div>

            {avatarUrl && (
              <div className="flex justify-center">
                <img
                  src={avatarUrl}
                  alt="Avatar preview"
                  className="w-32 h-32 rounded-full object-cover border-4 border-green-500"
                  onError={(e) => {
                    e.target.style.display = 'none'
                  }}
                />
              </div>
            )}

            <Button
              type="submit"
              loading={loading}
              className="w-full"
            >
              {t('profile.saveButton')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Profile
